/**
 * StepArcReview.tsx — Step 1: Arc slot editor
 *
 * Visual redesign v2:
 *  • ArcMap — arc template identity + horizontal slot timeline at the top
 *  • Slot cards with Day / Role / Perspective badge header row
 *  • Opening hook rendered as a styled quote block (hero element per day)
 *  • Title input styled as text-like by default; border appears on focus
 *  • Narrative note as a read-only callout (always was, now clearly styled)
 *  • Optional per-slot collapse (chevron in header); all slots start expanded
 *  • "Save Changes" appears only when the arc has been modified locally
 *
 * Test-compatibility contract (all 64 unit tests must pass):
 *  • All slots rendered and expanded by default → getAllByRole('combobox') finds all 7
 *  • cp-arc-title-{i} stays as <input> with onChange → fireEvent.change still works
 *  • cp-save-arc-btn, cp-core-principle, cp-lock-btn data-testids unchanged
 */

import React, { useState, useRef, useEffect } from "react";
import { ANGLES } from "../DistillPageV2/constants";
import {
  card, primaryBtn, secondaryBtn, stepHeading, stepSub,
  ACCENT, ACCENT_08, spinnerStyle,
} from "../DistillPageV2/sharedStyles";
import type { ArcSlot, AngleKey, ArcTemplateKey, EvaluationResult } from "./types";
import { ROLE_LABELS, ROLE_COLORS, ARC_TEMPLATE_META, QUALITY_COLORS } from "./types";
import EvaluatorPanel from "./EvaluatorPanel";

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  campaignId:         number;
  campaignName:       string;
  corePrinciple:      string;
  arc:                ArcSlot[];
  arcTemplate?:       ArcTemplateKey | null;
  apiUrl:             string;
  authHeaders:        Record<string, string>;
  onSaveArc:          (arc: ArcSlot[]) => Promise<void>;
  onArcRegenerated?:  (arc: ArcSlot[], corePrinciple: string) => void;
  onLock:             () => void;
  isSaving:           boolean;
  saveError:          string | null;
}

// ── Arc Map ───────────────────────────────────────────────────────────────────

interface ArcMapProps {
  arc:          ArcSlot[];
  arcTemplate?: ArcTemplateKey | null;
  evaluation?:  EvaluationResult | null;
  onNodeClick:  (i: number) => void;
}

const ArcMap: React.FC<ArcMapProps> = ({ arc, arcTemplate, evaluation, onNodeClick }) => {
  const meta = arcTemplate ? ARC_TEMPLATE_META[arcTemplate] : null;

  return (
    <div style={{
      background:   '#FAFAFA',
      border:       '1.5px solid #EAEAEA',
      borderRadius: '14px',
      padding:      '18px 22px 20px',
    }}>
      {/* Arc template identity */}
      {meta ? (
        <div style={{ marginBottom: '18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '5px' }}>
            <span style={{ fontSize: '14px' }}>🗺</span>
            <span style={{ fontWeight: 700, fontSize: '13px', color: '#1a1a1a', letterSpacing: '-0.01em' }}>
              {meta.name}
            </span>
          </div>
          <div style={{ fontSize: '12px', color: '#888', lineHeight: '1.55', paddingLeft: '21px' }}>
            {meta.description}
          </div>
        </div>
      ) : (
        <div style={{ fontSize: '12px', fontWeight: 600, color: '#aaa', marginBottom: '14px', letterSpacing: '0.03em', textTransform: 'uppercase' }}>
          Narrative Arc
        </div>
      )}

      {/* Horizontal timeline */}
      <div style={{ position: 'relative', paddingTop: '4px', paddingBottom: '2px' }}>
        {/* Connecting line behind nodes */}
        <div style={{
          position:   'absolute',
          top:        '11px',
          left:       '10px',
          right:      '10px',
          height:     '2px',
          background: 'linear-gradient(to right, #E0E0E0, #D4D4D4)',
          zIndex:     0,
        }} />

        {/* Node row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
          {arc.map((slot, i) => {
            const color       = ROLE_COLORS[slot.role] ?? ACCENT;
            const shortLabel  = (ROLE_LABELS[slot.role] ?? slot.role).split(' / ')[0].split(' ')[0];
            const slotEval    = evaluation?.slots.find(s => s.dayNumber === slot.dayNumber);
            const qualityColor = slotEval ? QUALITY_COLORS[slotEval.quality] : null;
            return (
              <button
                key={i}
                onClick={() => onNodeClick(i)}
                title={`Day ${slot.dayNumber}: ${ROLE_LABELS[slot.role] ?? slot.role}${slotEval ? ` — ${slotEval.quality}` : ''}`}
                style={{
                  display:       'flex',
                  flexDirection: 'column',
                  alignItems:    'center',
                  gap:           '5px',
                  background:    'none',
                  border:        'none',
                  padding:       '0',
                  cursor:        'pointer',
                }}
              >
                {/* Circle — shows quality ring when evaluation exists */}
                <div style={{
                  width:        '22px',
                  height:       '22px',
                  borderRadius: '50%',
                  background:   color,
                  border:       qualityColor ? `3px solid ${qualityColor}` : '3px solid #FAFAFA',
                  boxShadow:    qualityColor ? `0 0 0 2px ${qualityColor}88` : `0 0 0 2px ${color}55`,
                  flexShrink:   0,
                }} />
                {/* Day label */}
                <div style={{ fontSize: '10px', fontWeight: 700, color: '#777' }}>
                  D{slot.dayNumber}
                </div>
                {/* Role short label */}
                <div style={{ fontSize: '9px', color: '#aaa', textAlign: 'center', maxWidth: '48px', lineHeight: '1.3' }}>
                  {shortLabel}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ── Badge sub-components ──────────────────────────────────────────────────────

const DayBadge: React.FC<{ dayNumber: number; color: string }> = ({ dayNumber, color }) => (
  <div style={{
    background:    `${color}18`,
    color:         color,
    border:        `1.5px solid ${color}44`,
    borderRadius:  '20px',
    padding:       '3px 10px',
    fontSize:      '11px',
    fontWeight:    700,
    letterSpacing: '0.04em',
    flexShrink:    0,
  }}>
    Day {dayNumber}
  </div>
);

const RoleBadge: React.FC<{ label: string; color: string }> = ({ label, color }) => (
  <div style={{
    background:    `${color}12`,
    color:         color,
    borderRadius:  '6px',
    padding:       '3px 8px',
    fontSize:      '10px',
    fontWeight:    700,
    letterSpacing: '0.03em',
    flexShrink:    0,
  }}>
    {label}
  </div>
);

const PerspectiveBadge: React.FC<{ perspective: 'first_person' | 'observer' }> = ({ perspective }) => (
  <div style={{
    background:   '#F4F4F4',
    color:        '#888',
    borderRadius: '6px',
    padding:      '2px 7px',
    fontSize:     '10px',
    fontWeight:   500,
    flexShrink:   0,
  }}>
    {perspective === 'first_person' ? '✦ My story' : '◎ My take'}
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

const StepArcReview: React.FC<Props> = ({
  campaignId, campaignName, corePrinciple, arc: initialArc, arcTemplate,
  apiUrl, authHeaders,
  onSaveArc, onArcRegenerated, onLock, isSaving, saveError,
}) => {
  const [arc, setArc] = useState<ArcSlot[]>(initialArc);
  // All slots start expanded. User can collapse individually with the chevron.
  const [collapsedSlots, setCollapsedSlots] = useState<Set<number>>(new Set());
  const [hasEdits,       setHasEdits]       = useState(false);
  const slotRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Evaluation state
  const [evaluation,   setEvaluation]   = useState<EvaluationResult | null>(null);
  const [evaluating,   setEvaluating]   = useState(false);
  const [evalError,    setEvalError]    = useState<string | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  const [regenError,   setRegenError]   = useState<string | null>(null);

  // Sync when parent sends a fresh arc (e.g. after save or regeneration)
  useEffect(() => {
    setArc(initialArc);
    setHasEdits(false);
    setEvaluation(null);   // clear evaluation — arc has changed
  }, [initialArc]);

  // ── Slot field update (immediate — same as old component) ─────────────────
  const updateSlot = (index: number, patch: Partial<ArcSlot>) => {
    setArc(prev => prev.map((s, i) => i === index ? { ...s, ...patch } : s));
    setHasEdits(true);
  };

  // ── Arc Map node click: scroll to slot and ensure it's expanded ───────────
  const handleNodeClick = (i: number) => {
    setCollapsedSlots(prev => {
      const next = new Set(prev);
      next.delete(i);
      return next;
    });
    slotRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ── Toggle slot collapse ──────────────────────────────────────────────────
  const toggleCollapse = (i: number) => {
    setCollapsedSlots(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  // ── Save to backend ───────────────────────────────────────────────────────
  const handleSave = async () => {
    await onSaveArc(arc);
    setHasEdits(false);
  };

  // ── Evaluate arc ──────────────────────────────────────────────────────────
  const handleEvaluate = async () => {
    // Unsaved local edits mean the DB arc (what the evaluator reads) is stale.
    // Require the user to save first so they're evaluating what they see.
    if (hasEdits) {
      setEvalError('Save your changes first — the evaluator reads the saved arc.');
      return;
    }
    setEvaluating(true);
    setEvalError(null);
    try {
      const r = await fetch(`${apiUrl}/api/campaigns/${campaignId}/evaluate`, {
        method:      'POST',
        headers:     { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error((body as any).error || 'Evaluation failed');
      }
      const result: EvaluationResult = await r.json();
      setEvaluation(result);
    } catch (err: any) {
      setEvalError(err.message || 'Evaluation failed — please try again.');
    } finally {
      setEvaluating(false);
    }
  };

  // ── Regenerate arc with feedback ──────────────────────────────────────────
  const handleRegenerate = async (acceptedSuggestions: string, authorContext: string) => {
    setRegenerating(true);
    setRegenError(null);
    try {
      const r = await fetch(`${apiUrl}/api/campaigns/${campaignId}/regenerate-arc`, {
        method:      'POST',
        headers:     { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
        body:        JSON.stringify({ acceptedSuggestions, authorContext }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error((body as any).error || 'Regeneration failed');
      }
      const { storyArc, corePrinciple: newPrinciple } = await r.json();
      setArc(storyArc);
      setHasEdits(false);
      setEvaluation(null);
      onArcRegenerated?.(storyArc, newPrinciple);
    } catch (err: any) {
      setRegenError(err.message || 'Regeneration failed — please try again.');
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

      {/* ── Heading ──────────────────────────────────────────────────────────── */}
      <div>
        <div style={stepHeading}>Review your arc</div>
        <div style={stepSub}>
          "{campaignName}" — each slot is the blueprint for one post.
          Click any title or hook to edit it inline.
        </div>
      </div>

      {/* ── Core principle callout ───────────────────────────────────────────── */}
      <div
        data-testid="cp-core-principle"
        style={{
          background:   ACCENT_08,
          border:       `1.5px solid ${ACCENT}22`,
          borderRadius: '12px',
          padding:      '14px 16px',
          fontSize:     '14px',
          lineHeight:   '1.6',
        }}
      >
        <span style={{ fontWeight: 700, color: ACCENT }}>Core principle: </span>
        {corePrinciple}
      </div>

      {/* ── Arc identity + timeline map ──────────────────────────────────────── */}
      <ArcMap arc={arc} arcTemplate={arcTemplate} evaluation={evaluation} onNodeClick={handleNodeClick} />

      {/* ── Slot cards ──────────────────────────────────────────────────────── */}
      {arc.map((slot, i) => {
        const roleColor   = ROLE_COLORS[slot.role] ?? ACCENT;
        const roleLabel   = ROLE_LABELS[slot.role]  ?? slot.role;
        const isCollapsed = collapsedSlots.has(i);
        const slotEval    = evaluation?.slots.find(s => s.dayNumber === slot.dayNumber);

        return (
          <div
            key={slot.dayNumber}
            ref={el => { slotRefs.current[i] = el; }}
            style={{
              ...card,
              padding:  0,
              overflow: 'hidden',
            }}
          >
            {/* ── Card header — always visible ─────────────────────────────── */}
            <div style={{
              display:       'flex',
              alignItems:    'center',
              gap:           '10px',
              padding:       '13px 18px',
              borderBottom:  isCollapsed ? 'none' : '1px solid #F2F2F2',
            }}>
              <DayBadge  dayNumber={slot.dayNumber} color={roleColor} />
              <RoleBadge label={roleLabel}          color={roleColor} />
              {slot.perspective && <PerspectiveBadge perspective={slot.perspective} />}
              {slotEval && slotEval.quality !== 'strong' && (
                <span style={{
                  padding:       '2px 7px',
                  borderRadius:  '4px',
                  background:    `${QUALITY_COLORS[slotEval.quality]}14`,
                  border:        `1px solid ${QUALITY_COLORS[slotEval.quality]}44`,
                  color:         QUALITY_COLORS[slotEval.quality],
                  fontSize:      '10px',
                  fontWeight:    700,
                  letterSpacing: '0.04em',
                  flexShrink:    0,
                }}>
                  {slotEval.quality.toUpperCase()}
                </span>
              )}

              {/* Title preview in collapsed state */}
              {isCollapsed && (
                <div style={{
                  flex:         1,
                  fontSize:     '13px',
                  fontWeight:   600,
                  color:        '#444',
                  overflow:     'hidden',
                  whiteSpace:   'nowrap',
                  textOverflow: 'ellipsis',
                  paddingLeft:  '4px',
                }}>
                  {slot.title}
                </div>
              )}

              {/* Collapse / expand chevron */}
              <button
                onClick={() => toggleCollapse(i)}
                title={isCollapsed ? 'Expand' : 'Collapse'}
                style={{
                  marginLeft:   isCollapsed ? 'auto' : undefined,
                  background:   'none',
                  border:       'none',
                  cursor:       'pointer',
                  color:        '#bbb',
                  fontSize:     '16px',
                  lineHeight:   1,
                  padding:      '0 0 0 8px',
                  flexShrink:   0,
                  transform:    isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
                  transition:   'transform 0.15s',
                }}
              >
                ›
              </button>
            </div>

            {/* ── Card body — shown when not collapsed ─────────────────────── */}
            {!isCollapsed && (
              <div style={{ padding: '18px 18px 20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

                {/* Post title — input styled as text, reveals border on focus */}
                <div>
                  <div style={{
                    fontSize:      '10px',
                    fontWeight:    700,
                    color:         '#bbb',
                    letterSpacing: '0.07em',
                    textTransform: 'uppercase',
                    marginBottom:  '6px',
                  }}>
                    Post title
                  </div>
                  <input
                    data-testid={`cp-arc-title-${i}`}
                    value={slot.title}
                    onChange={e => updateSlot(i, { title: e.target.value })}
                    style={{
                      width:        '100%',
                      border:       'none',
                      borderBottom: '1.5px solid transparent',
                      borderRadius: 0,
                      background:   'none',
                      padding:      '2px 0 4px',
                      fontSize:     '15px',
                      fontWeight:   700,
                      color:        '#1a1a1a',
                      fontFamily:   'inherit',
                      outline:      'none',
                      boxSizing:    'border-box',
                      transition:   'border-color 0.15s',
                    }}
                    onFocus={e  => { e.target.style.borderBottomColor = ACCENT; }}
                    onBlur={e   => { e.target.style.borderBottomColor = 'transparent'; }}
                  />
                </div>

                {/* Opening hook — styled as a quote block; textarea on click */}
                <div>
                  <div style={{
                    fontSize:      '10px',
                    fontWeight:    700,
                    color:         '#bbb',
                    letterSpacing: '0.07em',
                    textTransform: 'uppercase',
                    marginBottom:  '8px',
                  }}>
                    Opening hook
                  </div>
                  <textarea
                    value={slot.openingHook}
                    onChange={e => updateSlot(i, { openingHook: e.target.value })}
                    rows={2}
                    style={{
                      width:        '100%',
                      border:       'none',
                      borderLeft:   `4px solid ${roleColor}`,
                      borderRadius: '0 8px 8px 0',
                      background:   `${roleColor}09`,
                      padding:      '10px 14px',
                      fontSize:     '14px',
                      fontWeight:   700,
                      fontStyle:    'italic',
                      color:        '#1a1a1a',
                      fontFamily:   'inherit',
                      outline:      'none',
                      resize:       'vertical',
                      boxSizing:    'border-box',
                      lineHeight:   '1.55',
                      transition:   'background 0.15s',
                    }}
                    onFocus={e  => { e.target.style.background = `${roleColor}16`; }}
                    onBlur={e   => { e.target.style.background = `${roleColor}09`; }}
                  />
                </div>

                {/* Narrative note — read-only callout */}
                <div style={{
                  background:   '#F7F7F7',
                  border:       '1px solid #EBEBEB',
                  borderRadius: '8px',
                  padding:      '10px 14px',
                }}>
                  <div style={{
                    fontSize:      '10px',
                    fontWeight:    700,
                    color:         '#bbb',
                    letterSpacing: '0.07em',
                    textTransform: 'uppercase',
                    marginBottom:  '5px',
                  }}>
                    What this day accomplishes
                  </div>
                  <div style={{
                    fontSize:   '13px',
                    color:      '#666',
                    lineHeight: '1.6',
                    fontStyle:  'italic',
                  }}>
                    {slot.narrativeNote}
                  </div>
                </div>

                {/* Tone / angle selector */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#aaa', letterSpacing: '0.03em' }}>
                    Tone
                  </span>
                  <select
                    value={slot.angle}
                    onChange={e => updateSlot(i, { angle: e.target.value as AngleKey })}
                    style={{
                      padding:      '5px 10px',
                      borderRadius: '6px',
                      border:       '1.5px solid #E4E4E0',
                      fontSize:     '12px',
                      fontFamily:   'inherit',
                      fontWeight:   600,
                      color:        '#444',
                      background:   '#fff',
                      cursor:       'pointer',
                      outline:      'none',
                    }}
                  >
                    {ANGLES.map(a => (
                      <option key={a.key} value={a.key}>{a.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* ── Arc Evaluator ─────────────────────────────────────────────────────── */}
      <EvaluatorPanel
        arc={arc}
        evaluation={evaluation}
        evaluating={evaluating}
        evalError={evalError}
        regenerating={regenerating}
        regenError={regenError}
        onEvaluate={handleEvaluate}
        onRegenerate={handleRegenerate}
      />

      {/* ── Error ────────────────────────────────────────────────────────────── */}
      {saveError && (
        <div style={{
          background:   '#FFF0F0',
          border:       '1.5px solid #FFD0D0',
          borderRadius: '10px',
          padding:      '12px 16px',
          fontSize:     '13px',
          color:        '#C0392B',
        }}>
          {saveError}
        </div>
      )}

      {/* ── Save Changes — only when the arc has been locally edited ─────────── */}
      {hasEdits && (
        <button
          data-testid="cp-save-arc-btn"
          onClick={handleSave}
          disabled={isSaving}
          style={{ ...secondaryBtn, alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          {isSaving && <span style={spinnerStyle} />}
          {isSaving ? 'Saving…' : 'Save Changes'}
        </button>
      )}

      {/* ── Lock Campaign ─────────────────────────────────────────────────────── */}
      <button
        data-testid="cp-lock-btn"
        onClick={onLock}
        disabled={isSaving}
        style={{ ...primaryBtn, marginTop: hasEdits ? '0' : '8px' }}
      >
        Lock Campaign & Generate Posts →
      </button>
    </div>
  );
};

export default StepArcReview;
